"use client";

import { ReactNode, useEffect, useMemo } from "react";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";

type LandingThemeProps = {
  children: ReactNode;
  className?: string;
  /**
   * Force applying the landing theme regardless of the current pathname.
   * Useful for route groups (e.g. admin) that should share the same theme.
   */
  force?: boolean;
};

const getLandingTheme = (_pathname: string | null) => "landing-shipany";

export default function LandingTheme({
  children,
  className,
  force = false,
}: LandingThemeProps) {
  const pathname = usePathname();
  const shouldApplyTheme = force || Boolean(pathname);
  const landingClassName = useMemo(
    () => cn(getLandingTheme(pathname), className),
    [pathname, className]
  );
  const landingWrapperClassName = useMemo(
    () => cn(landingClassName, "relative"),
    [landingClassName]
  );

  useEffect(() => {
    if (!shouldApplyTheme) return;
    const bodyClasses = landingClassName.split(" ").filter(Boolean);
    const body = document.body;
    body.classList.add(...bodyClasses);
    return () => {
      body.classList.remove(...bodyClasses);
    };
  }, [shouldApplyTheme, landingClassName]);

  return (
    <div
      className={shouldApplyTheme ? cn(landingWrapperClassName, "overflow-x-hidden") : ""}
    >
      {children}
    </div>
  );
}
